import * as React from 'react';
import { useMemo, useState } from 'react';
import { Box, Stack, Typography, Card, TextField, IconButton, Collapse, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import type { Task, LinkDef, TaskPhase, OID, DragSource } from '../types';
import {
  PHASE_LABELS, SYSTEM_METAKEYS, DEFAULT_COLLAPSED_PHASES, isNonMappablePhase, SALES_LOCK_TOOLTIP,
} from '../constants';
import { linksFor } from '../expressions';

export interface TaskColumnProps {
  tasks: Task[];
  links: LinkDef[];
  selectedOID: OID | null;
  selectedTaskId: number | null;
  onSelectTask: (id: number | null) => void;
  onHoverTask: (id: number) => void;
  onUnhoverTask: () => void;
  dragSource: DragSource | null;
  dropTaskId: number | null;
  onDragOverTask: (taskId: number, e: React.DragEvent) => void;
  onDragLeaveTask: (taskId: number) => void;
  onDrop: (taskId: number, e: React.DragEvent) => void;
  onUpdateTask: (taskId: number, patch: { metaKey?: string | null; unit?: string | null }) => void;
}

const PHASES = Object.keys(PHASE_LABELS) as TaskPhase[];

export function TaskColumn(props: TaskColumnProps): JSX.Element {
  const {
    tasks, links, selectedOID, selectedTaskId,
    onSelectTask, onHoverTask, onUnhoverTask,
    dropTaskId, onDragOverTask, onDragLeaveTask, onDrop,
    onUpdateTask,
  } = props;

  const litTaskIds = useMemo(() =>
    selectedOID
      ? new Set(linksFor(links, { itemOID: selectedOID }).flatMap(l => l.targetTaskIds))
      : null,
  [selectedOID, links]);

  // In-memory collapse state (resets on reload). Seeded from DEFAULT_COLLAPSED_PHASES (Sales
  // collapsed by default); every other phase starts expanded.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    DEFAULT_COLLAPSED_PHASES.forEach(p => { init[p] = true; });
    return init;
  });
  const togglePhase = (phase: TaskPhase) =>
    setCollapsed(prev => ({ ...prev, [phase]: !prev[phase] }));

  return (
    <Box sx={{ height: '100%', overflow: 'auto', bgcolor: 'background.paper' }}>
      <Box sx={{
        p: 2, pb: 1.25, borderBottom: 1, borderColor: 'divider',
        position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 2,
      }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
          Tasks &middot; {tasks.length}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          Drop a pill onto a task to create a link
        </Typography>
      </Box>

      <Box sx={{ px: 1, py: 1, pb: 10 }}>
        {PHASES.map(phase => {
          const inPhase = tasks.filter(t => t.phase === phase);
          if (!inPhase.length) return null;
          const isCollapsed = !!collapsed[phase];
          const locked = isNonMappablePhase(phase);
          return (
            <Box key={phase} sx={{ mb: 2 }}>
              <Stack
                direction="row" alignItems="center" spacing={0.25}
                onClick={() => togglePhase(phase)}
                role="button"
                aria-expanded={!isCollapsed}
                aria-label={`${PHASE_LABELS[phase]} phase`}
                sx={{
                  px: 0.5, pt: 0.75, pb: 0.5, cursor: 'pointer', userSelect: 'none',
                  borderRadius: 0.75, '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {isCollapsed
                  ? <KeyboardArrowRightIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  : <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                <Typography variant="overline" sx={{
                  color: 'text.secondary', fontWeight: 700, fontSize: 10.5, letterSpacing: 0.6,
                }}>
                  {PHASE_LABELS[phase]} &middot; {inPhase.length}
                </Typography>
                {locked && (
                  <Tooltip title={SALES_LOCK_TOOLTIP}>
                    <LockIcon sx={{ fontSize: 12, color: 'text.disabled', ml: 0.5 }} />
                  </Tooltip>
                )}
              </Stack>
              <Collapse in={!isCollapsed} unmountOnExit>
                {inPhase.map(t => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    taskLinks={linksFor(links, { taskId: t.id })}
                    isDrop={dropTaskId === t.id}
                    isLit={litTaskIds?.has(t.id) ?? false}
                    dim={litTaskIds ? !litTaskIds.has(t.id) : false}
                    isSel={selectedTaskId === t.id}
                    locked={locked}
                    onDragOver={e => onDragOverTask(t.id, e)}
                    onDragLeave={() => onDragLeaveTask(t.id)}
                    onDrop={e => onDrop(t.id, e)}
                    onClick={() => { if (!locked) onSelectTask(t.id === selectedTaskId ? null : t.id); }}
                    onMouseEnter={() => onHoverTask(t.id)}
                    onMouseLeave={onUnhoverTask}
                    onMetaKeyChange={v => onUpdateTask(t.id, { metaKey: v })}
                  />
                ))}
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

interface TaskRowProps {
  task: Task;
  taskLinks: LinkDef[];
  isDrop: boolean;
  isLit: boolean;
  dim: boolean;
  isSel: boolean;
  /** Non-mappable phase (e.g. Sales): grey-shaded, not-allowed cursor, read-only, drop blocked. */
  locked: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMetaKeyChange: (v: string | null) => void;
}

function TaskRow(p: TaskRowProps): JSX.Element {
  const { task, isDrop, isLit, dim, isSel, locked } = p;
  const mk = typeof task.additionalData === 'object' && task.additionalData
    ? (task.additionalData as Record<string, unknown>).metaKey as string | null ?? null
    : null;
  // Unit stamped by a populate link (additionalData.unit) — shown read-only alongside the MetaKey.
  const unit = typeof task.additionalData === 'object' && task.additionalData
    ? (task.additionalData as Record<string, unknown>).unit as string | null ?? null
    : null;

  // Locked (Sales) rows never take drop/select/lit highlighting — they can't be mapped.
  const borderColor = locked
    ? 'divider'
    : isDrop || isSel ? 'primary.main' : isLit ? 'success.main' : 'divider';

  const card = (
    <Card
      variant="outlined"
      data-locked={locked ? 'true' : undefined}
      onDragOver={p.onDragOver}
      onDragLeave={p.onDragLeave}
      onDrop={p.onDrop}
      onClick={p.onClick}
      onMouseEnter={p.onMouseEnter}
      onMouseLeave={p.onMouseLeave}
      sx={{
        mx: 0.75, mb: 0.5, p: 1.25,
        cursor: locked ? 'not-allowed' : 'pointer',
        borderColor,
        borderWidth: !locked && isDrop ? 2 : 1,
        borderStyle: !locked && isDrop ? 'dashed' : 'solid',
        bgcolor: locked ? 'action.disabledBackground' : (isDrop || isSel ? 'primary.50' : 'background.paper'),
        opacity: locked ? 0.6 : (dim ? 0.35 : 1),
        boxShadow: !locked && isDrop ? 3 : 0,
        transition: 'all .12s',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Typography variant="caption" sx={{
          fontFamily: 'JetBrains Mono, monospace',
          color: 'text.disabled',
          minWidth: 34,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}>
          #{task.taskNumber ?? task.id}
        </Typography>
        <Typography variant="body2" sx={{
          fontWeight: 600, flex: 1, lineHeight: 1.3,
          color: locked ? 'text.disabled' : 'text.primary',
        }}>
          {task.taskName}
        </Typography>
        {unit && (
          <Box
            title="Populate unit"
            sx={{
              flexShrink: 0,
              px: 0.75, py: 0.25, borderRadius: 0.75,
              bgcolor: 'action.selected', color: 'text.secondary',
              fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {unit}
          </Box>
        )}
        {/* Locked (Sales) rows are read-only: show an existing MetaKey as a static chip, no editor. */}
        {locked
          ? (mk && (
              <Box sx={{
                flexShrink: 0, px: 1, py: 0.25, borderRadius: 0.75,
                bgcolor: 'action.selected', color: 'text.disabled',
                fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, fontWeight: 600,
              }}>
                {mk}
              </Box>
            ))
          : <MetaKeyEditor value={mk} onChange={p.onMetaKeyChange} />}
      </Stack>
    </Card>
  );

  // Sales tasks explain why they can't be mapped, on hover and while dragging a pill over them.
  return locked ? <Tooltip title={SALES_LOCK_TOOLTIP} followCursor>{card}</Tooltip> : card;
}

interface MetaKeyEditorProps {
  value: string | null;
  onChange: (v: string | null) => void;
}

function MetaKeyEditor({ value, onChange }: MetaKeyEditorProps): JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value ?? '');

  React.useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim() || null;
    if (next !== (value ?? null)) onChange(next);
  };

  if (editing) {
    return (
      <TextField
        size="small"
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
        }}
        inputProps={{ list: 'naomi-mk-suggest' }}
        sx={{
          width: 140,
          '& input': { py: 0.25, fontSize: 11, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' },
        }}
        placeholder="MetaKey..."
      />
    );
  }

  if (!value) {
    return (
      <IconButton
        size="small"
        onClick={e => { e.stopPropagation(); setEditing(true); }}
        title="Set MetaKey"
        sx={{
          height: 22, px: 0.75, borderRadius: 0.75,
          border: 1, borderStyle: 'dashed', borderColor: 'primary.main',
          color: 'primary.dark', fontSize: 10.5,
          '&:hover': { bgcolor: 'primary.50' },
        }}
      >
        + set MetaKey
      </IconButton>
    );
  }

  const reserved = SYSTEM_METAKEYS.has(value);
  return (
    <Box
      onClick={e => { e.stopPropagation(); setEditing(true); }}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25,
        px: 1, py: 0.25, borderRadius: 0.75, cursor: 'pointer',
        bgcolor: 'primary.50', color: 'primary.dark',
        fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, fontWeight: 600,
      }}
    >
      {reserved && <LockIcon sx={{ fontSize: 10, mr: 0.25, opacity: 0.7 }} />}
      {value}
      <EditIcon sx={{ fontSize: 10, opacity: 0.6, ml: 0.25 }} />
    </Box>
  );
}
